import { ChangeEvent, useCallback, useMemo } from 'react';
import { Text } from '@nimbus-ds/components';
import { ProductRestrictionType } from '@tiendanube/common/src/enums';
import { Stack } from 'commons/components';
import { useCategories } from 'domains/Catalog/Categories/hooks';
import useTranslationShipping from 'domains/Shipping/useTranslationShipping';
import CategorySelectionSection from './CategorySelectionSection';
import ProductSelectionSection from './ProductSelectionSection';
import OptionsSection from '../../../OptionsSection';
import { RadioOption } from '../../../shared/types';

interface ProductRestrictionSectionProps {
  restrictedProducts: ProductRestrictionType;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  restrictedCategories?: number[];
  restrictedProductIds?: string[];
  onCategoriesChange: (categories: number[]) => void;
  onProductsChange: (variantIds: string[]) => void;
}

function ProductRestrictionSection({
  restrictedProducts,
  onChange,
  restrictedCategories = [],
  restrictedProductIds = [],
  onCategoriesChange,
  onProductsChange,
}: Readonly<ProductRestrictionSectionProps>): JSX.Element {
  const t = useTranslationShipping(
    'deliveryMethods.shippingLimiting.restrictions.restrictedProducts',
  );

  const { categories } = useCategories();

  const shouldDisableCategories = categories.length === 0;

  const restrictedProductsOptions: RadioOption[] = [
    {
      label: t('noRestriction'),
      value: ProductRestrictionType.NO_RESTRICTION,
    },
    {
      label: t('categories'),
      value: ProductRestrictionType.CATEGORIES,
      disabled: shouldDisableCategories,
    },
    {
      label: t('products'),
      value: ProductRestrictionType.PRODUCTS,
    },
  ];

  const handleChangeRestrictedProducts = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onChange(e);
    },
    [onChange],
  );

  const description = useMemo(() => {
    switch (restrictedProducts) {
      case ProductRestrictionType.NO_RESTRICTION:
        return t('descriptionNone');
      case ProductRestrictionType.CATEGORIES:
        return t('descriptionCategories');
      case ProductRestrictionType.PRODUCTS:
        return t('descriptionProducts');
      default:
        return '';
    }
  }, [restrictedProducts, t]);

  return (
    <Stack column align="stretch" spacing="tight">
      <Stack.Item>
        <OptionsSection
          title={t('title')}
          name="productRestrictionType"
          options={restrictedProductsOptions}
          value={restrictedProducts}
          onChange={handleChangeRestrictedProducts}
        />
      </Stack.Item>

      {!!description && (
        <Stack.Item>
          <Text>{description}</Text>
        </Stack.Item>
      )}

      {restrictedProducts === ProductRestrictionType.CATEGORIES && (
        <CategorySelectionSection
          categories={categories}
          selectedCategories={restrictedCategories}
          onCategoriesChange={onCategoriesChange}
          onChange={onChange}
        />
      )}

      {restrictedProducts === ProductRestrictionType.PRODUCTS && (
        <ProductSelectionSection
          selectedProductIds={restrictedProductIds}
          onProductsChange={onProductsChange}
          onChange={onChange}
        />
      )}
    </Stack>
  );
}

export default ProductRestrictionSection;
